import { useTranslation } from 'react-i18next'
import type { ThresholdNotification } from '../lib/types'
import type { NotificationsApi } from '../hooks/useNotifications'
import { NotificationItem } from './NotificationItem'

/** One collapsible category in the dropdown — the prototype's `groupHTML`
 * (`.notif-group-label`, `.notif-group-count`, `.notif-group-chev`). The
 * whole heading is the toggle; it sticks to the top of the scrolling list.
 * An empty category is not drawn at all. Collapse state is the caller's, so
 * it survives a re-render (e.g. after a dismiss). */
export function NotificationGroup({
  label,
  items,
  collapsed,
  onToggle,
  api,
  onNavigate,
}: Readonly<{
  label: string
  items: readonly ThresholdNotification[]
  collapsed: boolean
  onToggle: () => void
  api: Pick<NotificationsApi, 'setRead' | 'dismiss'>
  onNavigate?: () => void
}>) {
  const { i18n } = useTranslation()
  if (items.length === 0) return null
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="sticky top-0 z-[1] flex items-center gap-2 w-full px-3.5 pt-[9px] pb-2 border-0 border-b border-[var(--border-light)] cursor-pointer text-left text-[10.5px] leading-[normal] font-bold tracking-[.6px] uppercase text-[var(--text-tertiary)] bg-[var(--bg-card)] hover:text-[var(--text-secondary)]"
      >
        <span>{label}</span>
        <span className="text-[10px] leading-[normal] font-bold tracking-normal min-w-4 px-[5px] py-px rounded-[7px] text-center bg-[var(--bg-badge)] text-[var(--text-tertiary)]">
          {items.length.toLocaleString(i18n.language)}
        </span>
        <span
          className="ml-auto inline-flex transition-transform duration-150"
          style={{ transform: collapsed ? 'rotate(-90deg)' : undefined }}
        >
          <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {!collapsed && (
        <div>
          {items.map((n) => (
            <NotificationItem key={n.id} notification={n} onSetRead={api.setRead} onDismiss={api.dismiss} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  )
}
